import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Input, Space, message, DatePicker } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  DeleteOutlined 
} from "@ant-design/icons";
import {
  CreateLeaveApplicationData,
  GetLeaveApplicationData,
  UpdateLeaveApplicationData,
  DeleteLeaveApplicationData,  
} from "../../../AllAPI/StudentAPI";

const LeaveApplication = () => {
  const formDataFromStorage = JSON.parse(sessionStorage.getItem("studentData") || "{}");

  const RegId = formDataFromStorage.reg_id;
  const BranchId = formDataFromStorage.branch_id;
  const BoardingYear = formDataFromStorage.year;

  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  // FORM STATES
  const [reason, setReason] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // EDIT STATES
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const disablePastAndToday = (current) => {
  return current && current <= dayjs().endOf("day");
};



  // FETCH LEAVE DATA
  const fetchLeaveData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${GetLeaveApplicationData}/${RegId}/${BoardingYear}`);

      const formatted = res.data.map((item, index) => ({
        key: index + 1,
        id: item.id,
        reason: item.reason,
        from_date: item.from_date,
        to_date: item.to_date,
        status: item.status,
      }));

      setData(formatted);
    } catch (error) {
      message.error("Failed to load leave applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveData();
  }, []);

  // OPEN ADD MODAL
  const handleOpenModal = () => {
    setIsEdit(false);
    setReason("");
    setFromDate(null);
    setToDate(null);
    setSelectedId(null);
    setIsModalOpen(true);
  };

  // OPEN EDIT MODAL
  const handleEdit = (record) => {
    setIsEdit(true);
    setSelectedId(record.id);

    setReason(record.reason);
    setFromDate(dayjs(record.from_date));
    setToDate(dayjs(record.to_date));

    setIsModalOpen(true);
  };


  // SAVE OR UPDATE FUNCTION
  const handleSave = async () => {
    if (!reason || !fromDate || !toDate) {
      toast.error("Please fill all fields");
      return;
    }

    const payloadSave = {
      reason:reason,
      from_date: dayjs(fromDate).format("YYYY-MM-DD"),
      to_date: dayjs(toDate).format("YYYY-MM-DD"),
      reg_id: RegId,
      branch_id: BranchId,
      id: selectedId,
      year:BoardingYear
    };
    const payloadUpdate = {
      reason:reason,
      from_date: dayjs(fromDate).format("YYYY-MM-DD"),
      to_date: dayjs(toDate).format("YYYY-MM-DD"),
    };

    try {
      setLoading(true);

      if (isEdit) {
        const res = await axios.put(`${UpdateLeaveApplicationData}/${selectedId}`, payloadUpdate);
        if (res.data === "UPDATED") toast.success("Leave Updated Successfully");
      } else {
        const res = await axios.post(`${CreateLeaveApplicationData}`, payloadSave);
        if (res.data === "SAVED") toast.success("Leave Submitted Successfully");
      }

      fetchLeaveData();
      setIsModalOpen(false);
    } catch (error) {
      toast.error("Failed to save leave");
    } finally {
      setLoading(false);
    }
  };

  // STATUS LABEL
  const getStatusLabel = (status) => {
    if (status === 2) return <span className="shadow" style={{ color: "green",backgroundColor:"#d2faedff",borderRadius:"10px",padding:"5px 10px"  }}>Approved</span>;
    if (status === 3) return <span className="shadow" style={{ color: "red",backgroundColor:"#fad2d2ff",borderRadius:"10px",padding:"5px 10px"  }}>Rejected</span>;
     if (status === 0) return <span className="shadow" style={{ color: "#ff7301ff",backgroundColor:"#f7fdbaff",borderRadius:"10px",padding:"5px 10px" }}>Pending</span>;
  };

  // TABLE COLUMNS
  const columns = [
    { title: "No.", dataIndex: "key", width: 60 },
    { title: "Reason", dataIndex: "reason" },
    { title: "From Date", dataIndex: "from_date" },
    { title: "To Date", dataIndex: "to_date" },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => getStatusLabel(status),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>

          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            disabled={record.status === 2 || record.status === 3}   // <-- DISABLE WHEN STATUS = 2||3
          />
          <Button
          icon={<DeleteOutlined />}
          danger
          size="small"
          onClick={() => openDeleteModal(record.id)}
           disabled={record.status === 2 || record.status === 3}
        />


        </Space>
      ),
    },
  ];

  // FILTER
  const filteredData = data.filter((item) =>
    (item.reason ?? "").toLowerCase().includes(searchText.toLowerCase())
  );

  const openDeleteModal = (id) => {
  setDeleteId(id);
  setIsDeleteModalOpen(true);
};
  // DELETE FUNCTION
const confirmDelete = async () => {
  try {
    setLoading(true);
    await axios.delete(`${DeleteLeaveApplicationData}/${deleteId}`);

    toast.success("Leave deleted");
    fetchLeaveData();
  } catch (error) {
    toast.error("Failed to delete leave");
  } finally {
    setLoading(false);
    setIsDeleteModalOpen(false);
  }
};


  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-md-2"></div>
        <div className="col-12 col-md-8">
          <div className="header-row">
            <h2 className="page-title">Leave Application</h2>
          </div>

          {/* Search + Add Button */}
          <div className="search-bar">
            <Input
              placeholder="Search reason..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />

            <Button
              className="addButton mt-2 mt-md-0"              
              icon={<PlusOutlined />}
              onClick={handleOpenModal}
              style={{ marginLeft: 10 }}
            >
              Apply for Leave
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 10 }}
            bordered
            loading={loading}
            scroll={{ x: "max-content" }}
          />

          {/*SUBMIT-UPDATE MODAL */}
          <Modal
            title={isEdit ? "Edit Leave" : "Apply for Leave"}
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>,
              <Button key="save" className="addButton" onClick={handleSave}>
                {isEdit ? "Update" : "Submit"}
              </Button>,
            ]}
          >
            <div style={{ marginBottom: 12 }}>
              <label>From Date</label>
              <DatePicker
                style={{ width: "100%" }}
                value={fromDate}
                onChange={(date) => setFromDate(date)}
                format="DD-MM-YYYY"
                disabledDate={disablePastAndToday}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label>To Date</label>
              <DatePicker
                style={{ width: "100%" }}
                value={toDate}
                onChange={(date) => setToDate(date)}
                format="DD-MM-YYYY"
                disabledDate={disablePastAndToday}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label>Reason</label>
              <Input.TextArea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason"
              />
            </div>
          </Modal>

          {/*DELETE MODAL */}
          <Modal
            title="Confirm Delete"
            open={isDeleteModalOpen}
            onCancel={() => setIsDeleteModalOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>,
              <Button key="delete" danger type="primary" onClick={confirmDelete}>
                Delete
              </Button>,
            ]}
          >
            <p>Are you sure you want to delete this leave application?</p>
          </Modal>

        </div>
      </div>
    </div>
  );
};

export default LeaveApplication;
